import { StringifiableRecord } from 'query-string'
import { useCallback } from 'react'

import { useTrackProductInteraction } from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { SizeChartModalService } from '../../../../brunswick/containers/SizeChartModalService'
import { useSelectVariant } from '../../../../lib/hooks/useSelectVariant'
import { useProductHref } from '../../../../lib/routing'
import { Fit } from '../../../constants'

import type { Props, ViewModel } from './types'

export function useViewModel({ closeModal, quickBuyState }: Props): ViewModel {
  const { openSizeChartModal } = SizeChartModalService.useContainer()
  const { trackProductInteraction } = useTrackProductInteraction()
  const {
    quickBuy: { modal: quickBuyModalCopy },
  } = useTranslation()
  const {
    analyticsContext,
    availableProducts,
    currentSize,
    embroideryConfig,
    initialSelectedColor,
    modalState,
    productGroup,
    sizeChart,
    showColorSelector,
    swPromoEligible,
    variants,
  } = quickBuyState
  const isModalOpen = modalState === 'Open'

  const revealSizeChartModal = useCallback(() => {
    sizeChart && openSizeChartModal(sizeChart)
  }, [sizeChart, openSizeChartModal])

  const selectedVariant = {
    ...useSelectVariant({
      availableProducts,
      coloredAndFitVariants: variants,
      embroideryConfig,
      initialFeaturedProduct: productGroup.featuredProduct,
      initialSelectedColor,
      onCompleteAddToCart: closeModal,
      onCompleteViewProduct: closeModal,
      productGroup,
      shouldResetSelections: isModalOpen,
      swPromoEligible,
    }),
  }

  const query: StringifiableRecord = {
    color: selectedVariant.selectedColor?.rawName,
    fit:
      selectedVariant.availableFitsKeys && selectedVariant.availableFitsKeys.length > 0
        ? Fit.REGULAR
        : undefined,
  }

  const pdpLinkPath = useProductHref(productGroup.handle, query)

  const onProductView = () => {
    trackProductInteraction({
      handle: selectedVariant.productGroupHandle,
      color: selectedVariant.selectedColor?.rawName ?? '',
      shopifyId: selectedVariant.productShopifyId,
      id: selectedVariant.productId,
    })
    closeModal()
  }

  return {
    ...selectedVariant,
    analyticsContext,
    category: productGroup.featuredProduct?.category ?? '',
    currentSize: selectedVariant.currentSize ?? currentSize,
    closeModal,
    isModalOpen,
    pdpLinkPath,
    modalTitle: quickBuyModalCopy.headline,
    openSizeChartModal: revealSizeChartModal,
    sizeChart,
    showColorSelector,
    onProductView: onProductView,
  }
}
